import React, { useState, useMemo, useEffect } from 'react'
import { Popover, Tooltip, Typography } from 'antd'
import { Icon } from '@iconify/react'
import AssigneeSelector from './AssigneeSelector'
import {
    getAssigneeColor as sharedAssigneeColor,
    getAssigneeInitials as sharedAssigneeInitials,
    resolveAssigneeName,
} from './assigneeAvatar'

const { Text } = Typography

const AssigneeSelectorButton = ({
    selectedAssignee,
    onSelect,
    projectId,
    filteredAssignees,
    parentComponent,
    showName = false,
    taskListKind = 'support',
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    onTabNext,
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const isControlled = controlledOpen !== undefined
    const assigneePopoverOpen = isControlled
        ? Boolean(controlledOpen)
        : uncontrolledOpen

    const setAssigneePopoverOpen = (nextOpen) => {
        if (!isControlled) setUncontrolledOpen(nextOpen)
        controlledOnOpenChange?.(nextOpen)
    }

    /*
     * Derive the avatar from the shared helpers so this trigger and the picker
     * rows inside the popover always agree. Callers still pass getInitials /
     * getAssigneeColor props (every screen kept its own near-duplicate copy);
     * they are deliberately ignored now — letting each screen supply its own is
     * what allowed the same person to render two different avatars.
     */
    const getInitialsFn = sharedAssigneeInitials
    const getAssigneeColorFn = sharedAssigneeColor

    const selectedAssigneeData = useMemo(() => {
        if (!selectedAssignee) return null
        // Object from AssigneeSelector / Assign to Me / task.assigneeId[0]
        if (
            typeof selectedAssignee === 'object' &&
            (selectedAssignee.name ||
                selectedAssignee._id ||
                selectedAssignee.id)
        ) {
            return selectedAssignee
        }
        // Otherwise find in filteredAssignees
        if (filteredAssignees && Array.isArray(filteredAssignees)) {
            return filteredAssignees.find(
                (data) => data?.value === selectedAssignee
            )
        }
        return null
    }, [filteredAssignees, selectedAssignee])

    const handleAssigneeChange = (selected) => {
        onSelect?.(selected)
        setAssigneePopoverOpen(false)
    }

    const assigneeName = useMemo(() => {
        // For ProjectTaskList, selectedAssignee might be task.assigneeId[0]
        const source = selectedAssigneeData ?? selectedAssignee
        if (!source || typeof source !== 'object') return null
        return resolveAssigneeName(source) || null
    }, [selectedAssigneeData, selectedAssignee])

    const hasAssignee = Boolean(
        selectedAssigneeData ||
            (selectedAssignee &&
                typeof selectedAssignee === 'object' &&
                (selectedAssignee.name ||
                    selectedAssignee._id ||
                    selectedAssignee.id))
    )

    const profilePicture = useMemo(() => {
        if (selectedAssigneeData?.profilePicture) {
            return selectedAssigneeData.profilePicture
        }
        if (
            selectedAssignee &&
            typeof selectedAssignee === 'object' &&
            selectedAssignee.profilePicture
        ) {
            return selectedAssignee.profilePicture
        }
        return null
    }, [selectedAssigneeData, selectedAssignee])

    const [imageError, setImageError] = useState(false)

    // Reset image error when profilePicture changes
    useEffect(() => {
        setImageError(false)
    }, [profilePicture])

    return (
        <Popover
            content={
                <AssigneeSelector
                    onSelect={handleAssigneeChange}
                    onClose={() => setAssigneePopoverOpen(false)}
                    selectedAssignee={selectedAssigneeData}
                    projectId={projectId}
                    taskListKind={taskListKind}
                    open={assigneePopoverOpen}
                    onTabNext={onTabNext}
                />
            }
            trigger={onSelect ? 'click' : []}
            placement="bottomLeft"
            open={onSelect ? assigneePopoverOpen : false}
            onOpenChange={(open) => {
                if (!onSelect) return
                setAssigneePopoverOpen(open)
            }}
            arrow={false}
            styles={{
                body: {
                    padding: 0,
                },
                content: {
                    width: '400px',
                },
            }}
            getPopupContainer={() => document.body}
        >
            {showName ? (
                <div
                    onClick={(e) => {
                        e.stopPropagation()
                        if (!onSelect) return
                        setAssigneePopoverOpen(true)
                    }}
                    style={{
                        cursor: onSelect ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width:
                                parentComponent === 'taskDetailView'
                                    ? '28px'
                                    : '24px',
                            height:
                                parentComponent === 'taskDetailView'
                                    ? '28px'
                                    : '24px',
                            borderRadius: '50%',
                            backgroundColor: hasAssignee
                                ? getAssigneeColorFn(assigneeName)
                                : 'transparent',
                            color: hasAssignee ? '#ffffff' : '#475569',
                            fontSize: '11px',
                            fontWeight: 600,
                            border: hasAssignee ? 'none' : '1px dashed #cbd5e1',
                            flexShrink: 0,
                        }}
                    >
                        {hasAssignee ? (
                            profilePicture && !imageError ? (
                                <img
                                    src={profilePicture}
                                    alt={assigneeName || 'Assignee'}
                                    style={{
                                        width:
                                            parentComponent === 'taskDetailView'
                                                ? '28px'
                                                : '24px',
                                        height:
                                            parentComponent === 'taskDetailView'
                                                ? '28px'
                                                : '24px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                    }}
                                    onError={() => {
                                        setImageError(true)
                                    }}
                                />
                            ) : (
                                <Text
                                    style={{
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        fontFamily: 'var(--fontFamily)',
                                    }}
                                >
                                    {getInitialsFn(assigneeName)}
                                </Text>
                            )
                        ) : (
                            <Icon
                                icon="mi:user"
                                width={16}
                                height={16}
                                style={{ color: '#94a3b8' }}
                            />
                        )}
                    </div>
                    {hasAssignee && assigneeName && (
                        <Text
                            style={{
                                fontSize: '13px',
                                color: 'var(--textColorDarkSlate)',
                                fontWeight: 400,
                                fontFamily: 'var(--fontFamily)',
                            }}
                        >
                            {assigneeName}
                        </Text>
                    )}
                </div>
            ) : (
                <Tooltip
                    title={hasAssignee ? assigneeName : 'Assign this task'}
                    placement="top"
                >
                    <div
                        onClick={(e) => {
                            e.stopPropagation()
                            if (!onSelect) return
                            setAssigneePopoverOpen(true)
                        }}
                        style={{
                            cursor: onSelect ? 'pointer' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width:
                                parentComponent === 'taskDetailView'
                                    ? '28px'
                                    : '24px',
                            height:
                                parentComponent === 'taskDetailView'
                                    ? '28px'
                                    : '24px',
                            borderRadius: '50%',
                            backgroundColor: hasAssignee
                                ? getAssigneeColorFn(assigneeName)
                                : 'transparent',
                            color: hasAssignee ? '#ffffff' : '#475569',
                            fontSize: '11px',
                            fontWeight: 600,
                            border: hasAssignee ? 'none' : '1px dashed #cbd5e1',
                        }}
                    >
                        {hasAssignee ? (
                            profilePicture && !imageError ? (
                                <img
                                    src={profilePicture}
                                    alt={assigneeName || 'Assignee'}
                                    style={{
                                        width:
                                            parentComponent === 'taskDetailView'
                                                ? '28px'
                                                : '24px',
                                        height:
                                            parentComponent === 'taskDetailView'
                                                ? '28px'
                                                : '24px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                    }}
                                    onError={() => {
                                        setImageError(true)
                                    }}
                                />
                            ) : (
                                <Text
                                    style={{
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        fontFamily: 'var(--fontFamily)',
                                    }}
                                >
                                    {getInitialsFn(assigneeName)}
                                </Text>
                            )
                        ) : (
                            <Icon
                                icon="mi:user"
                                width={16}
                                height={16}
                                style={{ color: '#94a3b8' }}
                            />
                        )}
                    </div>
                </Tooltip>
            )}
        </Popover>
    )
}

export default AssigneeSelectorButton
